import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useField, useFormikContext } from "formik";
import { Tooltip, Icon, useBreakpoints } from "@qgiv/core-react";
import {
    FormikCheckbox,
    MoreInfoModal,
    TextButton,
    CheckboxWithTooltip,
    useHover,
} from "@qgiv/core-donor";
import { Ribbon } from "@qgiv/p2p-registration";
import ConnectedGoalInput from "./ConnectedGoalInput";
import "./FundraisingGoal.scss";
import CommitmentDetailsChip from "./CommitmentDetailsChip";

const ManageCheckbox = ({ label, isSmallScreen = false }) => {
    const [{ onBlur, onChange, ...field }] = useField({
        name: "Managed_By_Primary",
        type: "checkbox",
    });
    const checkboxProps = {
        ...field,
        id: "Managed_By_Primary",
        name: "Managed_By_Primary",
        handleChange: onChange,
        handleBlur: onBlur,
        innerPadding: false,
    };

    const deviceHasHover = useHover();

    return deviceHasHover ? (
        <FormikCheckbox {...checkboxProps}>
            {label}
            {isSmallScreen ? (
                <span className="-padding-left--10">
                    <MoreInfoModal>
                        They&apos;ll be added to your account and you&apos;ll
                        have access to their fundraising goal and page.
                    </MoreInfoModal>
                </span>
            ) : (
                <Tooltip
                    placement="right"
                    content="They'll be added to your account and you'll have access to their fundraising goal and page."
                    theme="dark"
                >
                    <Icon
                        className="-margin-left--5"
                        glyph="question-circle-regular"
                        type="FontAwesome"
                    />
                </Tooltip>
            )}
        </FormikCheckbox>
    ) : (
        <CheckboxWithTooltip
            {...checkboxProps}
            label={label}
            tooltipContent="They'll be added to your account and you'll have access to their fundraising goal and page."
        />
    );
};

ManageCheckbox.propTypes = {
    isSmallScreen: PropTypes.bool,
    label: PropTypes.string.isRequired,
};

const NfpCheckbox = ({ label }) => {
    const [{ onBlur, onChange, ...field }] = useField({
        name: "Is_Nfp",
        type: "checkbox",
    });
    const checkboxProps = {
        ...field,
        id: "Is_Nfp",
        handleChange: onChange,
        handleBlur: onBlur,
    };

    return <FormikCheckbox {...checkboxProps}>{label}</FormikCheckbox>;
};

NfpCheckbox.propTypes = {
    label: PropTypes.string.isRequired,
};

const NfpOnlyCategory = ({
    description = "You won't get a fundraising goal or page with this type of registration.",
    hasFundraisingCategories = false,
    multiCategory = false,
    multiCategoryLink = "Want to fundraise? Try changing your registration.",
}) => {
    const { setFieldValue } = useFormikContext();
    return (
        <div className="grid">
            <div className="col col--12 -padding-top--0">
                <p>{description}</p>
                {multiCategory && hasFundraisingCategories && (
                    <TextButton
                        handleClick={() => {
                            setFieldValue("Category", []);
                        }}
                        styles="-font-weight--bold -cursor--pointer"
                    >
                        <p className="-padding-top--5">{multiCategoryLink}</p>
                    </TextButton>
                )}
            </div>
        </div>
    );
};

NfpOnlyCategory.propTypes = {
    hasFundraisingCategories: PropTypes.bool,
    multiCategory: PropTypes.bool,
    description: PropTypes.string,
    multiCategoryLink: PropTypes.string,
};

const FundraisingGoal = ({
    allowsZeroDollarValue,
    categoryId,
    checkboxLabel,
    description,
    hasCommitment,
    hasFundraisingCategories = false,
    heading,
    isFundraisingOnly = false,
    isNfpOnly = false,
    isTeam = false,
    manageFundraisingCheckboxLabel = "",
    manageIsEnabled = false,
    nfpIsEnabled = false,
    nonFundraisingParticipantCheckboxDisclaimer,
    multiCategory = false,
    primaryRegistrantIsTeamCaptain = false,
    renderTeamGoalDescription,
    nonFundraisingParticipantOnlyLabel,
    nonFundraisingParticipantOnlyPrompt,
    commitmentDetailDisclaimers = {},
}) => {
    const { values } = useFormikContext();
    const { Is_Nfp, Participant_Fundraising_Goal, Managed_By_Primary } = values;
    const [goalActive, setGoalActive] = useState(false);

    useEffect(() => {
        if (Participant_Fundraising_Goal) {
            setGoalActive(true);
        } else {
            setGoalActive(false);
        }
    }, [Participant_Fundraising_Goal]);

    const { isSmallScreen } = useBreakpoints();
    return (
        <>
            {!isNfpOnly && <Ribbon>{heading}</Ribbon>}
            <div
                className={`fundraising-goal -callout ${
                    !isSmallScreen && !Is_Nfp
                        ? "-padding-bottom--0 -padding-right--0"
                        : ""
                }`}
            >
                <div className="grid">
                    <div
                        className={`col col--12  ${
                            Is_Nfp ? "col--sm-12 -padding-top--0" : "col--sm-9"
                        }`}
                    >
                        {!isNfpOnly && !Is_Nfp && (
                            <div className="grid">
                                <div className="col col--12 -padding-top--15">
                                    <p className="-font-weight--bold">
                                        {description}
                                    </p>
                                    {isTeam && renderTeamGoalDescription()}
                                </div>
                                <div className="col col--12 col--sm-8 -padding-bottom--15">
                                    <div className="fundraising-goal__input-container">
                                        <ConnectedGoalInput
                                            id="Participant-Fundraising-Goal"
                                            name="Participant_Fundraising_Goal"
                                            allowsZeroDollarValue={
                                                allowsZeroDollarValue
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        {manageIsEnabled && !Is_Nfp && !isNfpOnly && (
                            <div
                                className={
                                    !nfpIsEnabled ||
                                    (isFundraisingOnly &&
                                        manageIsEnabled &&
                                        !isSmallScreen)
                                        ? "-padding-top--15 -padding-bottom--15"
                                        : "-padding-top--15"
                                }
                            >
                                <ManageCheckbox
                                    isSmallScreen={isSmallScreen}
                                    label={manageFundraisingCheckboxLabel}
                                />{" "}
                            </div>
                        )}
                        {nfpIsEnabled &&
                            !isNfpOnly &&
                            !isFundraisingOnly &&
                            !primaryRegistrantIsTeamCaptain &&
                            !Managed_By_Primary && (
                                <div
                                    className={Is_Nfp ? "-padding-top--15" : ""}
                                >
                                    <NfpCheckbox label={checkboxLabel} />{" "}
                                </div>
                            )}
                        {nfpIsEnabled && !isNfpOnly && Is_Nfp && (
                            <p>{nonFundraisingParticipantCheckboxDisclaimer}</p>
                        )}
                        {nfpIsEnabled && isNfpOnly && (
                            <NfpOnlyCategory
                                multiCategory={multiCategory}
                                hasFundraisingCategories={
                                    hasFundraisingCategories
                                }
                                description={nonFundraisingParticipantOnlyLabel}
                                multiCategoryLink={
                                    nonFundraisingParticipantOnlyPrompt
                                }
                            />
                        )}
                    </div>
                    {!isSmallScreen && !Is_Nfp && (
                        <div className="col col--3 fundraising-goal__icon">
                            <Icon
                                className={`fundraising-goal__icon--sm ${goalActive ? "-color--theme-primary" : "-color--system-light"}`}
                                glyph="qgiv-fundraising"
                                type="Custom"
                            />
                        </div>
                    )}
                </div>
            </div>
            {hasCommitment && (
                <CommitmentDetailsChip
                    categoryId={categoryId}
                    location="fundraisingGoal"
                    managedByPrimary={Managed_By_Primary}
                    details={commitmentDetailDisclaimers}
                    classNames="-margin-top--10"
                />
            )}
        </>
    );
};

FundraisingGoal.propTypes = {
    allowsZeroDollarValue: PropTypes.bool.isRequired,
    categoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    checkboxLabel: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    hasCommitment: PropTypes.bool.isRequired,
    hasFundraisingCategories: PropTypes.bool.isRequired,
    heading: PropTypes.string.isRequired,
    isFundraisingOnly: PropTypes.bool,
    isNfpOnly: PropTypes.bool,
    isTeam: PropTypes.bool,
    // Label for displaying Manage by Primary checkbox
    manageFundraisingCheckboxLabel: PropTypes.string,
    // Flag for displaying Manage by Primary checkbox
    manageIsEnabled: PropTypes.bool,
    multiCategory: PropTypes.bool,
    nfpIsEnabled: PropTypes.bool,
    nonFundraisingParticipantCheckboxDisclaimer: PropTypes.string.isRequired,
    primaryRegistrantIsTeamCaptain: PropTypes.bool.isRequired,
    renderTeamGoalDescription: PropTypes.func.isRequired,
    nonFundraisingParticipantOnlyLabel: PropTypes.string.isRequired,
    nonFundraisingParticipantOnlyPrompt: PropTypes.string.isRequired,
    commitmentDetailDisclaimers: PropTypes.shape({}),
};

export default FundraisingGoal;
